"use client";

import { ChevronDown, Instagram, Youtube, Music2, Music } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const shopLinks = [{
  label: "Capital Solutions",
  href: "/CapitalSolutionsPage"
}, {
  label: "JV Partnership",
  href: "/JVPartnershipPage"
}, {
  label: "Referral Network",
  href: "/ReferralNetworkPage"
}, {
  label: "Pre-Qualification",
  href: "/PreQualificationFormPage"
}];
const supportLinks = [{
  label: "The BeneWealth Edge",
  href: "/TheBeneWealthEdgePage"
}, {
  label: "FAQ",
  href: "#"
}, {
  label: "Investor Portal",
  href: "#"
}, {
  label: "Contact",
  href: "#"
}];
const socialLinks = [{
  label: "Instagram",
  icon: Instagram,
  href: "#"
}, {
  label: "YouTube",
  icon: Youtube,
  href: "#"
}, {
  label: "TikTok",
  icon: Music2,
  href: "#"
}, {
  label: "Spotify",
  icon: Music,
  href: "#"
}];
const legalLinks = [{
  label: "Privacy",
  href: "#"
}, {
  label: "Terms",
  href: "#"
}, {
  label: "Compliance",
  href: "#"
}];
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[0, 1, 2, 3].map(i => <li key={i} data-index={i}>
            <div className="h-4 w-24 bg-muted animate-pulse" />
          </li>)}
      </ul>;
  }
  const items = menuItems.length > 0 ? menuItems : [{
    id: "1",
    label: "About Us",
    href: "/TheBeneWealthEdgePage"
  }, {
    id: "2",
    label: "Insights",
    href: "#"
  }, {
    id: "3",
    label: "Careers",
    href: "#"
  }, {
    id: "4",
    label: "Press",
    href: "#"
  }];
  return <ul className="space-y-3">
      {items.map((item, i) => <li data-index={i} key={item.id}>
          <Link to={item.href || "#"} className="group inline-flex items-center gap-2 text-sm font-default text-slate-muted hover:text-white transition-colors">
            <span className="h-1 w-1 bg-yellow-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            <span className="group-hover:translate-x-0.5 transition-transform">
              {item.label}
            </span>
          </Link>
        </li>)}
    </ul>;
}
export default function VaultFooter() {
  const {
    ref: wordmarkRef,
    isVisible: wordmarkVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2787" id="vault-footer" className="bg-slate-950 text-white pt-[120px] pb-[60px] px-6 md:px-10 lg:px-16 border-t border-slate-800 relative overflow-hidden">
      <div ref={wordmarkRef} className={`group relative mb-20 transition-all duration-1000 ${wordmarkVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <h2 className="font-default font-extrabold leading-[0.85] text-white select-none relative" style={{
        fontSize: "clamp(4rem, 12vw, 12rem)",
        letterSpacing: "-0.05em"
      }}>
          <span className="relative inline-block">
            BENEWEALTH
            <span className="text-yellow-500 group-hover:animate-pulse inline-block">
              .
            </span>
            <span aria-hidden="true" className="pointer-events-none absolute left-0 top-1/2 h-px w-0 group-hover:w-full bg-yellow-500/60 transition-[width] duration-1000 ease-linear" />
          </span>
        </h2>
      </div>

      <div ref={gridRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 transition-all duration-1000 delay-150 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div>
          <h3 className="font-default text-[11px] uppercase tracking-[0.15em] text-yellow-500 mb-6 font-semibold">
            Solutions
          </h3>
          <ul className="space-y-3">
            {shopLinks.map((link, i) => <li data-index={i} key={link.label}>
                <Link to={link.href} className="group inline-flex items-center gap-2 text-sm font-default text-slate-muted hover:text-white transition-colors">
                  <span className="h-1 w-1 bg-yellow-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-0.5 transition-transform">
                    {link.label}
                  </span>
                </Link>
              </li>)}
          </ul>
        </div>

        <div>
          <h3 className="font-default text-[11px] uppercase tracking-[0.15em] text-yellow-500 mb-6 font-semibold">
            Resources
          </h3>
          <ul className="space-y-3">
            {supportLinks.map((link, i) => <li data-index={i} key={link.label}>
                <Link to={link.href} className="group inline-flex items-center gap-2 text-sm font-default text-slate-muted hover:text-white transition-colors">
                  <span className="h-1 w-1 bg-yellow-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-0.5 transition-transform">
                    {link.label}
                  </span>
                </Link>
              </li>)}
          </ul>
        </div>

        <div>
          <h3 className="font-default text-[11px] uppercase tracking-[0.15em] text-yellow-500 mb-6 font-semibold">
            Company
          </h3>
          <MenuProvider menu_id="45">
            <FooterMenuConsumer />
          </MenuProvider>
        </div>

        <div>
          <h3 className="font-default text-[11px] uppercase tracking-[0.15em] text-yellow-500 mb-6 font-semibold">
            Connect
          </h3>
          <div className="flex items-center gap-3 mb-6">
            {socialLinks.map((social, i) => {
            const Icon = social.icon;
            return <Link data-index={i} key={social.label} to={social.href} aria-label={social.label} className="inline-flex items-center justify-center h-9 w-9 border border-slate-800 hover:border-yellow-500 hover:text-yellow-500 text-slate-muted transition-colors">
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                </Link>;
          })}
          </div>
          <p className="font-default text-sm text-white/80">
            <Link to="mailto:support@benewealth.info" className="hover:text-yellow-500 transition-colors">
              support@benewealth.info
            </Link>
          </p>
        </div>
      </div>

      <div className="mt-16 mb-8 h-px w-full bg-slate-800" />

      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="font-default text-[11px] uppercase tracking-[0.15em] text-slate-muted">
          © 2025 BeneWealth Financial. All Rights Reserved.
        </div>

        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.15em] font-default">
          {legalLinks.map((link, i) => <div data-index={i} key={link.label} className="flex items-center gap-3">
              <Link to={link.href} className="text-slate-muted hover:text-yellow-500 transition-colors">
                {link.label}
              </Link>
              {i < legalLinks.length - 1 && <span className="h-1 w-1 bg-yellow-500" aria-hidden="true" />}
            </div>)}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="border-slate-800 bg-transparent text-white hover:bg-slate-900 hover:text-white text-[11px] uppercase tracking-[0.15em] font-default h-9 px-3">
              EN / US
              <ChevronDown className="h-3 w-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800 text-white">
            <DropdownMenuItem className="text-[11px] uppercase tracking-[0.15em] font-default">
              EN / US
            </DropdownMenuItem>
            <DropdownMenuItem className="text-[11px] uppercase tracking-[0.15em] font-default">
              EN / UK
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </section>;
}